import { WaterDrop } from '@mui/icons-material';
import { useMemo, useState } from 'react';
import { DecarbContentLayout } from '../components/DecarbContentLayout';
import { DecarbOverviewHeader } from '../components/DecarbOverviewHeader';
import { FilterMenu, SearchBox, SortMenu } from '../components/TechToolbar';
import {
  EnergyKind,
  TechSortDimensions,
  TechSortState,
  WaterHeaterWithMetrics,
  getEnergyField,
  sortTechItemsByState,
} from '../types';
import { WaterHeaterUnit } from './WaterHeaterUnit';

type WaterHeaterSelectionDetailsProps = {
  helperTextComponent: React.ReactNode;
  availableWaterHeaters: WaterHeaterWithMetrics[];
  selectedWaterHeaters: (WaterHeaterWithMetrics & { count: number })[];
  costEnergyAndCoverageMetrics?: {
    // undefined: no stored figure to show yet (renders the placeholder)
    cost?: number;
    energyDemand: number;
    systemCoveragePercentage: number;
  };
  isLoading?: boolean;
  onAdd: (item: WaterHeaterWithMetrics) => void;
  onRemove: (item: WaterHeaterWithMetrics & { count: number }) => void;
  onChangeCount: (
    item: WaterHeaterWithMetrics & { count: number },
    newCount: number,
  ) => void;
  readOnly?: boolean;
};

export const WaterHeaterSelectionDetails: React.FC<
  WaterHeaterSelectionDetailsProps
> = ({
  helperTextComponent,
  availableWaterHeaters,
  selectedWaterHeaters,
  costEnergyAndCoverageMetrics,
  isLoading,
  onAdd,
  onRemove,
  onChangeCount,
  readOnly,
}) => {
  const { cost, energyDemand } = costEnergyAndCoverageMetrics || {};

  const [searchString, setSearchString] = useState('');
  const [sortState, setSortState] = useState<TechSortState>({
    dimension: TechSortDimensions[0],
    direction: 'asc',
  });
  const [filter, setFilter] = useState<EnergyKind[]>([]);

  const availableEnergyTypes = useMemo(() => {
    const types = new Set(
      availableWaterHeaters.map((item) => getEnergyField(item)).filter(Boolean),
    );
    return [...types] as EnergyKind[];
  }, [availableWaterHeaters]);

  const filteredAndSortedItems = useMemo(() => {
    let items = availableWaterHeaters;

    // Apply search filter
    if (searchString.length > 0) {
      items = items.filter((item) =>
        item.name.toLowerCase().includes(searchString.toLowerCase()),
      );
    }

    // Apply energy source filter (include only selected energy types)
    if (filter.length > 0) {
      items = items.filter((item) => {
        const energy = getEnergyField(item);
        return energy ? filter.includes(energy as EnergyKind) : true;
      });
    }

    // Apply sort
    items = sortTechItemsByState(items, sortState);

    return items;
  }, [availableWaterHeaters, searchString, filter, sortState]);

  return (
    <DecarbContentLayout
      availableItems={
        readOnly
          ? []
          : filteredAndSortedItems.map((item) => (
              <WaterHeaterUnit
                waterHeater={item}
                selected={false}
                actions={{ onAdd: () => onAdd(item) }}
              />
            ))
      }
      selectedItems={selectedWaterHeaters.map((item) => (
        <WaterHeaterUnit
          count={item.count}
          waterHeater={item}
          selected={true}
          isLoading={isLoading}
          readOnly={readOnly}
          actions={{
            onRemove: () => onRemove(item),
            onIncrement: () => onChangeCount(item, item.count + 1),
            onDecrement: () => onChangeCount(item, item.count - 1),
            onChangeCount: (newCount: number) => onChangeCount(item, newCount),
          }}
        />
      ))}
      overviewHeader={
        <DecarbOverviewHeader
          systemCoverageName={'Water Heater'}
          systemCoverageIcon={<WaterDrop />}
          systemCoverageHelperText={
            'Choose enough water heaters to get 100% coverage'
          }
          energyDemand={energyDemand}
          capitalCost={cost}
          coveragePercentage={
            costEnergyAndCoverageMetrics?.systemCoveragePercentage
          }
        />
      }
      helperText={helperTextComponent}
      searchComponent={
        readOnly ? undefined : (
          <SearchBox query={searchString} setQuery={setSearchString} />
        )
      }
      filterComponent={
        readOnly ? undefined : availableEnergyTypes.length > 1 ? (
          <FilterMenu
            listOfAllOptions={availableEnergyTypes}
            selectedOptions={filter}
            onReset={() => setFilter([])}
            onSelect={(option: EnergyKind) =>
              setFilter((prev) =>
                prev.includes(option)
                  ? prev.filter((val) => val !== option)
                  : [...prev, option],
              )
            }
          />
        ) : undefined
      }
      sortComponent={
        readOnly ? undefined : (
          <SortMenu
            dimensions={TechSortDimensions}
            activeSort={sortState}
            onSortChange={setSortState}
          />
        )
      }
    />
  );
};
